<template>
  <v-container class="fill-height" fluid>
    <v-row justify="center">
      <v-col cols="12" sm="10" md="6">
        <v-card class="pa-5" style="opacity: 0.88">
          <v-card-text class="text-center orange--text" v-if="checking">
            <p v-if="!errorMessage">{{ $t("activation-verification-in-progress") }}</p>
            <div class="m-4 red--text" v-if="errorMessage">{{ errorMessage }}</div>
          </v-card-text>
          <v-card-text v-else>
            <p class="text-center green--text">{{ $t("email-verification-successful") }}</p>
            <div class="title text-center">{{ $t("register-terarra-user-agent") }}</div>
            <v-window v-model="data.step">
              <v-window-item :value="'register'">
                <v-card-text>
                  <h3>{{ $t("company-agent-details") }}</h3>
                  <v-form aria-autocomplete="off" autocomplete="off" @submit.prevent="register" v-on:submit.prevent>
                    <input type="hidden" name="activationKey" v-model="activationKey" />

                    <v-text-field autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false"
                      :label="$t('full-name')" name="name" prepend-icon="mdi-account" v-model="user.name" type="text"
                      :rules="[rules.required]" required></v-text-field>

                    <v-text-field autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false"
                      :label="$t('phone-number')" name="phoneNumber" prepend-icon="mdi-phone" v-model="user.phone"
                      type="text" :rules="[rules.required]" required></v-text-field>

                    <v-select autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false"
                      :label="$t('preferred-language')" name="defaultLanguage" prepend-icon="mdi-translate"
                      v-model="user.defaultLanguage" :items="languages" ></v-select>

                    <v-text-field autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false"
                      :label="$t('company-name')" name="companyName" prepend-icon="mdi-bank" v-model="user.companyName"
                      type="text"></v-text-field>

                    <v-textarea autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false"
                      :label="$t('company-address')" name="companyAddress" prepend-icon="mdi-map-marker"
                      v-model="user.companyAddress" rows="3"></v-textarea>

                    <h3 class="mt-5">{{ $t("user-login-details") }}</h3>

                    <p>
                      {{ $t("username") }}: <b>{{ user.email }}</b>
                    </p>
                    {{ $t("set-password") }}:
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-text-field id="password" :label="$t('password')" v-model="user.password" name="password"
                          prepend-icon="mdi-lock" @keyup.enter="register()"
                          :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                          :rules="[rules.required, rules.min, rules.strength]" validate-on-blur
                          :type="showPassword ? 'text' : 'password'" @click:append="showPassword = !showPassword"
                          :hint="$t('password-hint')" loading>
                          <template v-slot:progress>
                            <v-progress-linear absolute height="7" :color="checkPasswordStrength <= 4 ? 'red' : 'green'"
                              :value="checkPasswordStrength * 20"></v-progress-linear>
                          </template>
                        </v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field id="confirmPassword" :label="$t('confirm-password')"
                          v-model="user.confirmPassword" name="confirmPassword" prepend-icon="mdi-lock" type="password"
                          @keyup.enter="register()"></v-text-field>
                      </v-col>
                      <v-col> </v-col>
                    </v-row>
                  </v-form>
                </v-card-text>
              </v-window-item>
              <v-window-item :value="'register-done'">
                <v-card-text>
                  <h4>{{ $t("registration-successful-pending-activation") }}</h4>
                  <p>{{ $t("registration-successful-message") }}</p>
                  <p>{{ $t("check-spam-folder-message") }}</p>
                </v-card-text>
              </v-window-item>
            </v-window>

            <div class="m-4 red--text" v-if="errorMessage">{{ errorMessage }}</div>

            <v-btn color="primary" v-if="data.step == 'register'" :disabled="processing" @keyup.enter="register()"
              type="submit" @click="register()">{{ processing ? $t("registering") : $t("register") }}</v-btn>

            <v-spacer></v-spacer>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            {{ $t("already-have-an-account") }} <v-btn to="/login" color="secondary" type="button" text>{{ $t("login")
              }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import {Component, Vue} from "vue-property-decorator";
import {cacheSrv} from "../../libs/cache/cache";
import router from "./../../router";
import {registerService} from "./register-service";
import {profileService} from "./../../libs/user/profile-service";
import {userService} from "./../../libs/user/user-service";
import {config, utils} from "./../../libs/utils";
import axios from "axios";

@Component({
  components: {},
})
export default class Register extends Vue {
  ssoApiUrl = config.VUE_APP_SSO_API;
  homeUrl = config.VUE_APP_APP_URL;

  showPassword = false;
  languages = config.VUE_APP_LANGUAGES;

  rules = {
    required: (value) => !!value || (this.$t("this-field-is-required") as string),
    min: (v) => v.length >= 8 || (this.$t("use-8-characters-or-more-for-your-password") as string),
    strength: (v) => {
      return this.checkPasswordStrength >= 5 || (this.$t("please-choose-a-stronger-password") as string);
    },
  };

  user = {
    activationKey: "",
    username: "",
    password: "",
    companyName: "",
    name: "",
    phone: "",
    email: "",
    companyAddress: "",
    confirmPassword: "",
    defaultLanguage: "",
  };

  data: any = {
    step: "register",
  };

  checking = true;
  submitted = false;
  processing = false;
  errorMessage = "";
  passwordError = false;
  error = {
    msg: "",
  };

  activationKey = "";

  constructor() {
    super();

    //load activation key from url path
    this.activationKey = this.$route.params.key;

    console.log("this.activationKey", this.activationKey);

    //check if activation key is valid
    if (this.activationKey) {
      setTimeout(() => {
        this.verifyActivationKey();
      }, 1000);
    }

    this.init()
  }

  async init() {
  }

  async verifyActivationKey() {
    console.log("verifyActivationKey");
    this.errorMessage = "";
    this.processing = true;
    this.checking = true;
    let result;

    try {
      result = await registerService.verifyActivationKey(this.activationKey);
      console.log("result", result);
      this.processing = false;
      if (result) {
        let data = result;

        if (data.error) {
          console.log("Error in verifying activation key");
          this.errorMessage = data.message;
          return;
        }

        if (data["success"]) {
          this.errorMessage = "";
          this.checking = false;
          this.user.email = data.email;
          this.user.username = data.email;
          this.user.activationKey = this.activationKey;
        } else {
          console.log("failed to load activation key");
          this.errorMessage = this.$t("invalid-activation-key") as string;
        }
      } else {
        console.log("Error in validating activation key. No data");
      }
    } catch (e) {
      console.log("error verifying activation key", e);
      this.processing = false;
      this.errorMessage = this.$t("error-processing-your-request") as string;
      return;
    }
  }

  async register() {
    //Clean up stored profile
    cacheSrv.remove("profile");

    console.log("register");
    this.errorMessage = "";
    this.processing = true;
    let result;

    if (!this.user.companyName || !this.user.name || !this.user.companyAddress || !this.user.username || !this.user.password || !this.user.confirmPassword || !this.user.defaultLanguage) {
      this.errorMessage = this.$t("please-fill-in-all-fields") as string;
      this.processing = false;
      return;
    }
    //add check of password and confirm password
    if (this.user.password !== this.user.confirmPassword) {
      this.errorMessage = this.$t("password-and-confirm-password-do-not-match") as string;
      this.processing = false;
      return;
    }

    if (this.checkPasswordStrength < 5) {
      this.errorMessage = this.$t("please-choose-a-stronger-password") as string;
      this.processing = false;
      return;
    }

    try {
      result = await registerService.register(this.user);
      this.processing = false;
      if (result) {
        let data = result;

        if (data.error) {
          this.errorPass();
          console.log("Error in registration");
          this.errorMessage = data.message;
          return;
        }

        if (data["token"]) {
          this.errorMessage = "";
          await userService.initUser();
          await profileService.authorize();

          setTimeout(function () {
            router.push("/");
          }, 888);
        } else {
          console.log("no token");
          this.errorPass();
        }
      } else {
        console.log("Error in validating username. No data");
      }
    } catch (e) {
      console.log("error logging in", e);
      this.errorPass();
      this.processing = false;
      this.errorMessage = this.$t("error-processing-your-request") as string;
      return;
    }
  }

  errorPass() {
    this.passwordError = true;
    setTimeout(() => {
      this.passwordError = false;
    }, 1600);
  }

  get checkPasswordStrength() {
    let password = this.user.password;
    let strength = 0;
    if (password.match(/[a-z]+/)) {
      strength += 1;
    }
    if (password.match(/[A-Z]+/)) {
      strength += 1;
    }
    if (password.match(/[0-9]+/)) {
      strength += 1;
    }
    //if (password.match(/[$@#&!]+/)) {
      strength += 1;
    //}
    if (password.length > 8) {
      strength += 1;
    }
    return strength;
  }
}
</script>
